import React, { useState, useEffect } from "react";
import { Menu, Tooltip } from "antd";
import { NavLink } from "react-router-dom";
import CustomScrollbars from "../../util/CustomScrollbars";
import SidebarLogo from "./SidebarLogo";
import { THEME_TYPE_LITE } from "../../constants/ThemeSetting";
import { useSelector, useDispatch } from "react-redux";
import "./menu.css";
import {
  convertToMenuStructure,
  getMenuIcon,
  toCamelCase,
} from "../../util/utility-methods";
import { FunnelPlotOutlined } from "@ant-design/icons";
import IntlMessages from "../../util/IntlMessages";
import {
  setSelectedTemplateID,
  setSelectedMainGroupID,
} from "../../appRedux/actions";

const SidebarContent = ({ sidebarCollapsed, setSidebarCollapsed, auth }) => {
  const { themeType } = useSelector(({ settings }) => settings);
  const dispatch = useDispatch();
  const pathname = useSelector(({ common }) => common.pathname);
  const sidebarSearchTarget = useSelector(
    ({ common }) => common.sidebarSearchTarget,
  );
  const templatesNav = useSelector(
    ({ templateRetrieval }) => templateRetrieval.assignedTemplates,
  );
  const mainGroupNav = useSelector(
    ({ templateRetrieval }) => templateRetrieval.assignedMainGroup,
  );
  const user = auth?.User;
  const menuInfo = user.MenuInfo;
  const { SubMenu } = Menu;

  const selectedKeys = pathname.substr(1);
  const defaultOpenKeys = selectedKeys.split("/")[1];

  const [openKeys, setOpenKeys] = useState(
    defaultOpenKeys ? [defaultOpenKeys] : [],
  );

  useEffect(() => {
    if (sidebarSearchTarget && sidebarSearchTarget.openKeys) {
      setOpenKeys((prevKeys) => {
        const merged = new Set([...prevKeys, ...sidebarSearchTarget.openKeys]);
        return Array.from(merged);
      });
    }
  }, [sidebarSearchTarget]);

  const menuArray = convertToMenuStructure(menuInfo);
  const hasTemplateRetrieval = menuInfo.some(
    (menu) => menu.PName === "Template Retrieval",
  );

  const MenuTitle = ({ title, sidebarCollapsed }) => (
    <Tooltip title={title} placement="right" mouseEnterDelay={0.5}>
      {title}
    </Tooltip>
  );

  return (
    <>
      <SidebarLogo
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
      />
      <div className="gx-sidebar-content">
        <CustomScrollbars className="gx-layout-sider-scrollbar">
          <Menu
            openKeys={openKeys}
            onOpenChange={(keys) => setOpenKeys(keys)}
            selectedKeys={[selectedKeys]}
            theme={themeType === THEME_TYPE_LITE ? "lite" : "dark"}
            mode="inline"
            style={{ marginBottom: "20px" }}
          >
            {menuArray &&
              menuArray.length &&
              menuArray.map((menu) => {
                if (menu.Mname === "Templates") return <></>;
                return (
                  <SubMenu
                    key={toCamelCase(menu.Mname)}
                    icon={
                      <Tooltip
                        title={menu.Mname}
                        placement="right"
                        mouseEnterDelay={0.5}
                      >
                        {getMenuIcon(menu.MIcon)}
                      </Tooltip>
                    }
                    title={
                      <MenuTitle
                        title={menu.Mname}
                        sidebarCollapsed={sidebarCollapsed}
                      />
                    }
                  >
                    {menu?.Pages &&
                      menu?.Pages.length &&
                      menu.Pages.map((page) =>
                        page.PageId ? (
                          <Menu.Item key={toCamelCase(page.PName)}>
                            <Tooltip
                              title={page.PName}
                              placement="right"
                              mouseEnterDelay={0.5}
                            >
                              <NavLink
                                to={page.PageURL}
                                activeClassName="selected-navlink"
                              >
                                <span>{page.PName}</span>
                              </NavLink>
                            </Tooltip>
                          </Menu.Item>
                        ) : (
                          <SubMenu
                            key={toCamelCase(page.Mname)}
                            icon={
                              <Tooltip
                                title={page.Mname}
                                placement="right"
                                mouseEnterDelay={0.5}
                              >
                                {getMenuIcon(page.MIcon)}
                              </Tooltip>
                            }
                            title={
                              <MenuTitle
                                title={page.Mname}
                                sidebarCollapsed={sidebarCollapsed}
                              />
                            }
                            className="nested-sub-menu"
                          >
                            {page?.Pages &&
                              page?.Pages.length &&
                              page.Pages.map((subPage) => (
                                <Menu.Item key={toCamelCase(subPage.PName)}>
                                  <Tooltip
                                    title={subPage.PName}
                                    placement="right"
                                    mouseEnterDelay={0.5}
                                  >
                                    <NavLink
                                      to={subPage.PageURL}
                                      activeClassName="selected-navlink"
                                    >
                                      <span>{subPage.PName}</span>
                                    </NavLink>
                                  </Tooltip>
                                </Menu.Item>
                              ))}
                          </SubMenu>
                        ),
                      )}
                  </SubMenu>
                );
              })}
            {hasTemplateRetrieval && (
              <SubMenu
                key="template-sidebar"
                icon={
                  <Tooltip
                    title={<IntlMessages id="sidebar.templates" />}
                    placement="right"
                    mouseEnterDelay={0.5}
                  >
                    <FunnelPlotOutlined />
                  </Tooltip>
                }
                title={
                  <MenuTitle
                    title={<IntlMessages id="sidebar.templates" />}
                    sidebarCollapsed={sidebarCollapsed}
                  />
                }
              >
                {templatesNav.map((templatesNavData) => {
                  if (templatesNav.length > 0) {
                    return (
                      <SubMenu
                        key={"TempId=" + templatesNavData.EmrTemplateID}
                        title={
                          <MenuTitle
                            title={templatesNavData.TemplateName}
                            sidebarCollapsed={sidebarCollapsed}
                          />
                        }
                      >
                        {mainGroupNav.map((outerData) => {
                          if (outerData.length > 0) {
                            if (
                              outerData[0].TemplateID ===
                              templatesNavData.EmrTemplateID
                            ) {
                              return outerData.map((innerData) => {
                                return (
                                  <Menu.Item
                                    key={"MainGroupId=" + innerData.MaingroupID}
                                  >
                                    <Tooltip
                                      title={innerData.MaingroupName}
                                      placement="right"
                                      mouseEnterDelay={0.5}
                                    >
                                      <NavLink
                                        to="TemplateRetrieval"
                                        activeClassName="selected-navlink"
                                        onClick={() => {
                                          dispatch(
                                            setSelectedTemplateID(
                                              templatesNavData.EmrTemplateID,
                                            ),
                                          );
                                          dispatch(
                                            setSelectedMainGroupID(
                                              innerData.MaingroupID,
                                            ),
                                          );
                                        }}
                                      >
                                        <span>{innerData.MaingroupName}</span>
                                      </NavLink>
                                    </Tooltip>
                                  </Menu.Item>
                                );
                              });
                            }
                          }
                        })}
                      </SubMenu>
                    );
                  }
                })}
              </SubMenu>
            )}
          </Menu>
          {!sidebarCollapsed && (
            <p
              style={{
                position: "sticky",
                bottom: "0px",
                left: "12px",
                top: "95%",
                paddingLeft: "24px",
                backgroundColor: "#29348e",
                color: "#fafafa",
                fontSize: "12px",
              }}
            >
              © {new Date().getFullYear()} All rights reserved.
            </p>
          )}
        </CustomScrollbars>
      </div>
    </>
  );
};

export default React.memo(SidebarContent);
